import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ImageGenerator from '../common/ImageGenerator';
import useCustomNotification from '../../hooks/useCustomNotification';

/**
 * ImageGeneratorDialog Component
 * 
 * A dialog that contains the ImageGenerator component
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {function} props.onClose - Function to call when dialog should close
 * @param {string} props.projectId - Optional project ID for saving images
 * @param {boolean} props.compactLayout - Whether to use a compact layout (default: false)
 * @param {string} props.title - Custom title for the dialog (default: "Generate Images")
 */
const ImageGeneratorDialog = ({ 
  open, 
  onClose, 
  projectId,
  compactLayout = false,
  title = "Generate Images"
}) => {
  const { showNotification } = useCustomNotification();

  const handleImageGenerated = (result) => {
    showNotification({
      message: 'Image generated successfully!',
      type: 'success'
    });
  };

  const handleImageSaved = (savedImage) => {
    showNotification({
      message: 'Image saved successfully!',
      type: 'success'
    });
    
    // Close dialog after saving
    if (onClose) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={compactLayout ? "sm" : "md"}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {title}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ mt: 2, p: compactLayout ? 2 : 3 }}>
        {!compactLayout && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Create AI-generated images by describing what you want to see. Images can be saved after generation.
            </Typography>
          </Box>
        )}
        
        <ImageGenerator
          projectId={projectId}
          onImageGenerated={handleImageGenerated}
          onImageSaved={handleImageSaved}
          showSaveOption={!!projectId}
          showFullControls={!compactLayout}
          compactLayout={compactLayout}
          defaultOptions={{
            model: 'gpt-image-1',
            size: '1024x1024',
            style: 'natural'
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ImageGeneratorDialog;